import { AxiosMethods } from '@setvi/shared/enums';
import { Product } from '@setvi/shared/interfaces';
import { axiosHelper } from '@setvi/shared/services';
import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

enum Endpoints {
  Price = '/pricing/get',
  PriceGroup = '/pricing-groups/pricing'
}

export enum PriceType {
  Price = 'Price',
  PriceGroup = 'PriceGroup'
}

export enum PricingType {
  Quote = 'quote',
  Order = 'order'
}

interface MutationProps {
  type?: PriceType;
  body: {
    type?: PricingType;
    accountNumber: string;
    products: {
      sku: string;
      quantity: number;
    }[];
  };
}

type MinimalProduct = Pick<Product, 'sku' | 'quantity'> & { id?: string };

export interface GetPricesProps {
  type?: PriceType;
  pricingType?: PricingType;
  productList: MinimalProduct[];
  accountNumber: string;
}

export const getProductsPrices = () => ({
  mutationFn: ({ type, body }: MutationProps) =>
    axiosHelper({
      endpoint: Endpoints[(type || PriceType.Price) as keyof typeof Endpoints],
      method: AxiosMethods.POST,
      body,
      baseURL: process.env.REVOPS_API
    })
});

export const useProductsPrices = () => {
  const { mutationFn } = getProductsPrices();
  const mutation = useMutation(mutationFn);

  const getPrices = async ({
    type = PriceType.Price,
    pricingType,
    productList,
    accountNumber
  }: GetPricesProps) => {
    try {
      const products = productList.map(product => ({
        sku: product.sku,
        quantity: product.quantity || 1
      }));

      const response: { pricing: Product[] } = await mutation.mutateAsync({
        type,
        body: {
          accountNumber,
          products,
          ...(pricingType && { type: pricingType })
        }
      });

      return response?.pricing?.map((productPricing, idx) => ({
        ...productPricing,
        id: productList[idx]?.id || null,
        price: Number(productPricing.sell)
      }));
    } catch (error) {
      enqueueSnackbar('Error occured while fetching products price', {
        variant: 'error',
        autoHideDuration: 8000
      });
      return null;
    }
  };

  return { getPrices, isLoading: mutation.isLoading };
};
